import { useAuth } from '@/lib/auth/use-auth.js';
import { t } from '@/lib/i18n/messages.js';
import { tokens } from '@/lib/tokens.js';
import type { CSSProperties, ReactNode } from 'react';

interface NavItem {
  labelKey:
    | 'admin.nav_templates'
    | 'admin.nav_tenant_templates'
    | 'admin.nav_roles'
    | 'admin.nav_grants'
    | 'admin.nav_audit_log';
  href: string;
  requireSuperAdmin?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { labelKey: 'admin.nav_templates', href: '/templates', requireSuperAdmin: true },
  { labelKey: 'admin.nav_tenant_templates', href: '/tenant-templates' },
  { labelKey: 'admin.nav_roles', href: '/roles' },
  { labelKey: 'admin.nav_grants', href: '/grants' },
  { labelKey: 'admin.nav_audit_log', href: '/audit-log' },
];

const styles = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: tokens.colors.bgDark,
    fontFamily: tokens.font.sans,
  } as CSSProperties,
  sidebar: {
    width: '220px',
    flexShrink: 0,
    backgroundColor: '#0f172a',
    borderRight: `1px solid ${tokens.colors.borderDark}`,
    padding: '1.5rem 0',
    display: 'flex',
    flexDirection: 'column' as const,
  } as CSSProperties,
  sidebarTitle: {
    padding: '0 1rem 1rem',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
  } as CSSProperties,
  navLink: (active: boolean): CSSProperties => ({
    display: 'block',
    padding: '0.625rem 1rem',
    fontSize: '0.875rem',
    color: active ? '#ffffff' : tokens.colors.textMutedDark,
    backgroundColor: active ? tokens.colors.primary : 'transparent',
    textDecoration: 'none',
    fontWeight: active ? 600 : 400,
    borderLeft: active ? '3px solid #ffffff' : '3px solid transparent',
    transition: 'all 0.2s ease',
  }),
  content: {
    flex: 1,
    padding: '2rem',
    overflowY: 'auto' as const,
  } as CSSProperties,
};

interface AdminShellProps {
  children: ReactNode;
  currentPath?: string;
}

export function AdminShell({ children, currentPath = '' }: AdminShellProps) {
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const visibleItems = NAV_ITEMS.filter((item) => !item.requireSuperAdmin || isSuperAdmin);

  return (
    <div style={styles.shell}>
      <nav style={styles.sidebar} aria-label="Admin navigation">
        <div style={styles.sidebarTitle}>Admin</div>
        {visibleItems.map((item) => {
          const active = currentPath.startsWith(item.href);
          return (
            <a
              key={item.href}
              href={item.href}
              style={styles.navLink(active)}
              aria-current={active ? 'page' : undefined}
            >
              {t(item.labelKey)}
            </a>
          );
        })}
      </nav>
      <main style={styles.content}>{children}</main>
    </div>
  );
}
